import * as Dialog from '@radix-ui/react-dialog';
import {
  BarChart3,
  BookOpen,
  CircleHelp,
  ClipboardCheck,
  ChartLine,
  Home,
  ListChecks,
  Landmark,
  Menu,
  Radar,
  Settings,
  ShieldAlert,
  Upload,
} from 'lucide-react';
import { forwardRef } from 'react';
import { NavLink, Outlet, type NavLinkProps } from 'react-router-dom';

import { usePendingReviews } from '../../features/reviews/queries';
import { cn } from '../../lib/utils';

const primaryNav = [
  { to: '/dashboard', label: 'Dashboard', icon: Home },
  { to: '/trade-plan', label: 'Plan', icon: ShieldAlert },
  { to: '/positions', label: 'Positions', icon: BarChart3 },
  { to: '/analytics', label: 'Analytics', icon: ChartLine },
  { to: '/journal', label: 'Journal', icon: BookOpen },
] as const;

const overflowNav = [
  { to: '/investing', label: 'Investing', icon: Landmark },
  { to: '/market-console', label: 'Market Console', icon: Radar },
  { to: '/reviews', label: 'Reviews', icon: ClipboardCheck },
  { to: '/import', label: 'Import', icon: Upload },
  { to: '/rules', label: 'Rules', icon: ListChecks },
  { to: '/reviews/weekly', label: 'Weekly Review', icon: ClipboardCheck },
  { to: '/settings', label: 'Settings', icon: Settings },
  { to: '/help', label: 'Help', icon: CircleHelp },
] as const;

const allNav = [...primaryNav, ...overflowNav] as const;
type NavItem = (typeof allNav)[number];

export function AppShell() {
  return (
    <div className="min-h-screen bg-bg text-text lg:pl-64">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-border bg-bg-muted px-4 py-5 lg:block">
        <NavBrand />
        <nav className="mt-8 space-y-1" aria-label="Primary">
          {allNav.map((item) => (
            <ShellLink key={item.to} item={item} />
          ))}
        </nav>
      </aside>
      <div className="pb-20 lg:pb-0">
        <Outlet />
      </div>
      <BottomNav />
    </div>
  );
}

function NavBrand() {
  return (
    <div>
      <div className="text-sm font-semibold uppercase tracking-wide text-text-muted">
        Disciplined Trader
      </div>
      <div className="mt-2 rounded border border-success/40 bg-success/10 px-3 py-2 text-sm text-success">
        Trade gate active
      </div>
    </div>
  );
}

type ShellLinkProps = Omit<NavLinkProps, 'children' | 'className' | 'to'> & {
  item: NavItem;
};

const ShellLink = forwardRef<HTMLAnchorElement, ShellLinkProps>(function ShellLink(
  { item, ...props },
  ref,
) {
  const Icon = item.icon;
  const pending = usePendingReviews();
  const pendingCount = item.to === '/reviews' ? (pending.data?.count ?? 0) : 0;
  return (
    <NavLink
      ref={ref}
      to={item.to}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded px-3 py-2 text-sm transition hover:bg-surface',
          isActive ? 'bg-surface-raised text-text' : 'text-text-muted',
        )
      }
      {...props}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      <span>{item.label}</span>
      {pendingCount > 0 ? (
        <span className="ml-auto min-w-5 rounded-full bg-danger px-1.5 py-0.5 text-center text-[11px] font-semibold text-white">
          {pendingCount > 99 ? '99+' : pendingCount}
        </span>
      ) : null}
    </NavLink>
  );
});

function BottomNav() {
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-bg/95 px-2 py-2 backdrop-blur lg:hidden">
      <nav className="mx-auto grid max-w-lg grid-cols-6 gap-1" aria-label="Mobile primary">
        {primaryNav.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex min-h-12 flex-col items-center justify-center rounded px-1 text-[11px] transition',
                  isActive ? 'bg-surface-raised text-text' : 'text-text-muted hover:bg-surface',
                )
              }
              aria-label={item.label}
            >
              <Icon className="h-5 w-5" aria-hidden="true" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
        <MoreSheet />
      </nav>
    </div>
  );
}

function MoreSheet() {
  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className="flex min-h-12 flex-col items-center justify-center rounded px-1 text-[11px] text-text-muted transition hover:bg-surface"
          aria-label="Open more navigation"
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
          <span>More</span>
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 rounded-t-lg border border-border bg-bg p-4 shadow-xl">
          <Dialog.Title className="text-base font-semibold">More</Dialog.Title>
          <div className="mt-4 grid gap-2">
            {overflowNav.map((item) => (
              <Dialog.Close key={item.to} asChild>
                <ShellLink item={item} />
              </Dialog.Close>
            ))}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
